import { hoverHighlight, clearHoverHighlight } from './useTrackHighlight';

/** tag.firstClass · "text…" — a label a human can match to the canvas. */
export function nodeLabel(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const cls = (el.getAttribute('class') ?? '').split(/\s+/).filter(Boolean)[0];
  const txt = (el.textContent ?? '').trim().replace(/\s+/g, ' ');
  // Truncate at a word boundary so labels never end mid-word.
  const short =
    txt.length > 24 ? `${txt.slice(0, 24).replace(/\s+\S*$/, '')}…` : txt;
  return `${tag}${cls ? `.${cls}` : ''}${short ? ` · "${short}"` : ''}`;
}

/**
 * Crosshair pick mode: hovering nodes inside the scoped frame highlights them
 * (dashed overlay via useTrackHighlight); clicking one picks it. Esc or the
 * returned stop() cancels. Capture-phase listeners so the canvas's own click
 * handling (frame select/drag) never fires for the picking click.
 */
export function startPickOnCanvas(
  frameId: string,
  onPick: (cfNodeId: string, label: string) => void,
): () => void {
  const resolve = (target: EventTarget | null): { cfId: string; el: Element } | null => {
    if (!(target instanceof Element)) return null;
    const el = target.closest('[data-cf-id]');
    if (!el || !el.closest(`[data-frame-id="${frameId}"]`)) return null;
    return { cfId: el.getAttribute('data-cf-id')!, el };
  };
  const onMove = (e: PointerEvent) => {
    const hit = resolve(e.target);
    if (hit) hoverHighlight(frameId, hit.cfId);
    else clearHoverHighlight();
  };
  const onClick = (e: MouseEvent) => {
    const hit = resolve(e.target);
    if (!hit) return;
    e.preventDefault();
    e.stopPropagation();
    onPick(hit.cfId, nodeLabel(hit.el));
    stop();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') stop();
  };
  const stop = () => {
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('click', onClick, true);
    window.removeEventListener('keydown', onKey, true);
    clearHoverHighlight();
    document.body.style.cursor = '';
  };
  window.addEventListener('pointermove', onMove, true);
  window.addEventListener('click', onClick, true);
  window.addEventListener('keydown', onKey, true);
  document.body.style.cursor = 'crosshair';
  return stop;
}
